import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { DocumentService } from '../document/document.service';
import {
  LogicielCaisseReasortTransfertService,
  ReasortTransfertCounts,
} from '../logiciel-caisse/logiciel-caisse-reasort-transfert.service';
import { appendErpUserStationParams } from '../erp-api-query.helper';

export interface ErpAchatDashboardPayload {
  success: boolean;
  filters: {
    station_id: number;
    date_from: string;
    date_to: string;
  };
  kpis: {
    commande_fournisseur: { period: number; pending: number };
    bon_reception: {
      period: number;
      pending_a_receptionner: number;
      pending_en_controle: number;
    };
    facture_fournisseur: {
      period: number;
      pending_non_payee: number;
      pending_brouillon: number;
    };
    retour_fournisseur: { period: number; pending: number };
    avoir_fournisseur: { period: number; pending: number };
    pending_actions_total: number;
  };
  series: {
    commande_fournisseur: { day: string; count: number }[];
    bon_reception: { day: string; count: number }[];
    facture_fournisseur: { day: string; count: number }[];
    retour_fournisseur: { day: string; count: number }[];
    avoir_fournisseur: { day: string; count: number }[];
  };
  error?: string;
}

export interface FacturesFournisseurGlobalesStats {
  total_ht: number;
  total_ttc: number;
  factures_payees: { nombre: number; total_ttc: number };
  factures_a_payer: {
    nombre: number;
    total_ttc: number;
    detail: {
      factures_non_payees: { nombre: number; total_ttc: number };
      montant_restant_factures_partiellement_payees: {
        nombre: number;
        montant_restant: number;
      };
    };
  };
  factures_partiellement_payees?: {
    nombre: number;
    total_ttc: number;
    montant_restant: number;
  };
  total_factures: number;
}

export interface AchatDashboardBundle {
  erp: ErpAchatDashboardPayload | null;
  facturesGlobales: FacturesFournisseurGlobalesStats | null;
  reassort: ReasortTransfertCounts | null;
}

@Injectable({ providedIn: 'root' })
export class AchatDashboardService {
  private readonly erpUrl = `${String(environment.document_url || environment.apiUrl).replace(
    /\/$/,
    ''
  )}/api/documents/achat/dashboard-stats`;

  constructor(
    private readonly http: HttpClient,
    private readonly documentService: DocumentService,
    private readonly reasortService: LogicielCaisseReasortTransfertService
  ) {}

  fetchErpStats(params: {
    stationId: number;
    dateFrom: string;
    dateTo: string;
  }): Observable<ErpAchatDashboardPayload> {
    let hp = new HttpParams()
      .set('date_from', params.dateFrom)
      .set('date_to', params.dateTo);
    hp = appendErpUserStationParams(hp, { stationId: params.stationId });
    return this.http.get<ErpAchatDashboardPayload>(this.erpUrl, { params: hp }).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Statistiques ERP indisponibles');
        }
        return res;
      }),
      catchError((err) =>
        throwError(
          () =>
            new Error(
              (err?.error?.error as string) ||
                err?.message ||
                'Erreur chargement statistiques achat'
            )
        )
      )
    );
  }

  fetchBundle(params: {
    stationId: number;
    dateFrom: string;
    dateTo: string;
    includeReassort: boolean;
  }): Observable<AchatDashboardBundle> {
    const erp$ = this.fetchErpStats({
      stationId: params.stationId,
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
    }).pipe(catchError(() => of(null)));

    const factures$ = this.documentService.getStatsFacturesFournisseurGlobales().pipe(
      map((res) => (res?.success ? res.data ?? null : null)),
      catchError(() => of(null))
    );

    const reassort$ = params.includeReassort
      ? this.reasortService.getCounts().pipe(catchError(() => of(null)))
      : of(null);

    return forkJoin({ erp: erp$, facturesGlobales: factures$, reassort: reassort$ });
  }
}
